// err-breadcrumbs-v1 — action-breadcrumb ring buffer (pure core).
//
// Shared contract with the /api/client-events ingest + error_tickets pipeline:
// crash and console_error events carry a TOP-LEVEL `breadcrumbs` field on the
// POST body (sibling of `payload`, NOT inside it — the ingest caps/stringifies
// `payload` into payload_json, so contract fields must ride the body root):
//
//   breadcrumbs: Array<{ t: number; kind: 'route'|'click'|'fetch-fail'|'visibility'; detail: string }>
//
//   - t       epoch ms
//   - detail  <= 120 chars, NEVER form input values / query strings / auth material
//   - last 25 entries, oldest first
//
// This module is framework-agnostic and DOM-free so the vitest node-env suite
// (npm test — CI + pre-deploy gate) can guard the contract. DOM glue lives in
// src/reliability.ts.

export const BREADCRUMBS_VERSION = 'err-breadcrumbs-v1'
export const MAX_BREADCRUMBS = 25
export const MAX_BREADCRUMB_DETAIL = 120
export const MAX_CLICK_TEXT = 40

export type BreadcrumbKind = 'route' | 'click' | 'fetch-fail' | 'visibility'

export type Breadcrumb = {
  t: number
  kind: BreadcrumbKind
  detail: string
}

export type BreadcrumbTrail = {
  record: (kind: BreadcrumbKind, detail: unknown) => void
  list: () => Breadcrumb[]
  clear: () => void
}

// Clamp arbitrary input into a safe detail string (single line, <= max chars).
export function clampDetail(detail: unknown, max = MAX_BREADCRUMB_DETAIL): string {
  let text = ''
  try {
    text = typeof detail === 'string' ? detail : String(detail ?? '')
  } catch {
    text = ''
  }
  const collapsed = text.replace(/\s+/g, ' ').trim()
  if (collapsed.length <= max) return collapsed
  return `${collapsed.slice(0, Math.max(0, max - 1))}…`
}

// Ring buffer of the user's last actions. record() never throws; list()
// returns copies so queued events can't be mutated after the fact.
export function createBreadcrumbTrail(limit = MAX_BREADCRUMBS, now: () => number = () => Date.now()): BreadcrumbTrail {
  const entries: Breadcrumb[] = []
  return {
    record(kind: BreadcrumbKind, detail: unknown) {
      try {
        entries.push({ t: now(), kind, detail: clampDetail(detail) })
        if (entries.length > limit) entries.splice(0, entries.length - limit)
      } catch { /* breadcrumbs must never break the app */ }
    },
    list() {
      return entries.map((entry) => ({ ...entry }))
    },
    clear() {
      entries.length = 0
    },
  }
}

export type ClickTargetDescriptor = {
  tag?: string
  id?: string
  className?: string
  // Trimmed VISIBLE text only. The DOM glue must never pass input/textarea/
  // select values or [data-sensitive]/contenteditable content here.
  text?: string
}

// Compact `tag#id.firstClass "Visible text"` description of a clicked element.
export function formatClickDetail(descriptor: ClickTargetDescriptor): string {
  const tag = (descriptor.tag || 'unknown').toLowerCase().slice(0, 24)
  let selector = tag
  const id = (descriptor.id || '').trim()
  if (id) selector += `#${id.slice(0, 40)}`
  const firstClass = (descriptor.className || '').trim().split(/\s+/).filter(Boolean)[0] || ''
  if (firstClass) selector += `.${firstClass.slice(0, 40)}`
  const text = clampDetail(descriptor.text || '', MAX_CLICK_TEXT)
  return clampDetail(text ? `${selector} "${text}"` : selector)
}

// Reduce a fetch URL to its path — no query string, no fragment, no origin
// (unless cross-origin, where the host is kept for context). Never throws.
export function fetchUrlPath(url: unknown): string {
  let raw = ''
  try {
    raw = typeof url === 'string' ? url : String(url ?? '')
  } catch {
    raw = ''
  }
  raw = raw.trim()
  if (!raw) return '(unknown)'
  // Opaque schemes carry payloads in the URL itself — never include them.
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(raw)?.[1]?.toLowerCase()
  if (scheme === 'data' || scheme === 'blob' || scheme === 'javascript') return `${scheme}:`
  try {
    const parsed = new URL(raw, 'http://relative.invalid')
    const path = parsed.pathname || '/'
    const sameOrigin = parsed.host === 'relative.invalid'
    return sameOrigin ? path : `//${parsed.host}${path}`
  } catch {
    // Not URL-parseable: strip query/fragment by hand.
    return raw.split(/[?#]/)[0] || '(unknown)'
  }
}

// `GET /api/crm/deals -> 500` | `POST /api/crm/leads -> network`
export function formatFetchFailDetail(method: unknown, url: unknown, outcome: unknown): string {
  const verb = clampDetail(method || 'GET', 10).toUpperCase() || 'GET'
  const path = fetchUrlPath(url)
  return clampDetail(`${verb} ${path} -> ${clampDetail(outcome ?? 'failed', 24)}`)
}
